import axiosInstance from '../../services/api';
import { API_ENDPOINTS } from '../feature/sprayCalculator/constants';

/**
 * Spray Calculator API Service
 * Handles all API calls related to the Spray Calculator feature
 */
export class SprayCalculatorService {
  /**
   * Fetches available liquid volume units (for tank volume)
   * @returns {Promise<Array>} List of liquid volume units
   */
  static async getLiquidVolumeUnits() {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.LIQUID_VOLUME_UNITS);
      return data.results || data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching liquid volume units:', error);
      throw error;
    }
  }

  /**
   * Fetches available area units
   * @returns {Promise<Array>} List of area units
   */
  static async getAreas() {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.AREAS);
      return data.results || data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching areas:', error);
      throw error;
    }
  }

  /**
   * Fetches available measurement units (for row spacing, band width)
   * @returns {Promise<Array>} List of measurement units
   */
  static async getMeasurementUnits() {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.MEASUREMENT_UNITS);
      return data.results || data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching measurement units:', error);
      throw error;
    }
  }

  /**
   * Fetches available water volume per area units
   * @returns {Promise<Array>} List of water volume per area units
   */
  static async getWaterVolumePerAreas() {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.WATER_VOLUME_PER_AREAS);
      return data.results || data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching water volume per area units:', error);
      throw error;
    }
  }

  /**
   * Fetches product dose units with optional filters
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.dose_type] - Filter by dose type
   * @param {string} [params.unit_form] - Filter by unit form
   * @returns {Promise<Array>} List of product dose units
   */
  static async getProductDoses(params = {}) {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.PRODUCT_DOSES, { params });
      return data.results || data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching product doses:', error);
      throw error;
    }
  }

  /**
   * Searches commercial products
   * @param {Object} [params={}] - Search parameters
   * @param {string} [params.search] - Search term
   * @param {number} [params.page] - Page number
   * @param {number} [params.page_size] - Page size
   * @returns {Promise<Object>} Search results with products
   */
  static async searchCommercialProducts(params = {}) {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.COMMERCIAL_PRODUCTS, { params });
      if (data.results) {
        return { results: data.results, hasMore: data.has_next || false };
      }
      return { results: data, hasMore: false };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error searching commercial products:', error);
      throw error;
    }
  }

  /**
   * Calculates spray volumes based on input parameters
   * @param {Object} payload - Calculator input data
   * @param {number} payload.tank_volume - Tank volume
   * @param {string} payload.tank_volume_unit_id - Tank volume unit ID
   * @param {number} payload.total_area - Total area
   * @param {string} payload.area_unit_id - Area unit ID
   * @param {string} payload.application_type - Application type (full_surface or band)
   * @param {number} [payload.band_percentage] - Band percentage (for band application)
   * @param {number} [payload.row_spacing] - Row spacing (for band application)
   * @param {string} [payload.row_spacing_unit_id] - Row spacing unit ID
   * @param {number} [payload.band_width] - Band width (for band application)
   * @param {string} [payload.band_width_unit_id] - Band width unit ID
   * @param {number} payload.water_volume - Water volume per area
   * @param {string} payload.water_volume_unit_id - Water volume per area unit ID
   * @param {Array<Object>} payload.products - Products with doses
   * @returns {Promise<Object>} Calculation results
   */
  static async calculate(payload) {
    try {
      const { data } = await axiosInstance.post(API_ENDPOINTS.CALCULATE, payload);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calculating spray:', error);
      throw error;
    }
  }
}

/**
 * Query keys factory for React Query cache management
 * Provides consistent keys for caching and invalidation
 */
export const sprayCalculatorKeys = {
  all: ['sprayCalculator'],
  liquidVolumeUnits: () => [...sprayCalculatorKeys.all, 'liquidVolumeUnits'],
  areas: () => [...sprayCalculatorKeys.all, 'areas'],
  measurementUnits: () => [...sprayCalculatorKeys.all, 'measurementUnits'],
  waterVolumePerAreas: () => [...sprayCalculatorKeys.all, 'waterVolumePerAreas'],
  productDoses: (params) => [...sprayCalculatorKeys.all, 'productDoses', params],
  productsSearch: (params) => [...sprayCalculatorKeys.all, 'productsSearch', params],
  calculation: (payload) => [...sprayCalculatorKeys.all, 'calculation', payload],
};
